<template>
  <div class="message-input-container">
    <v-snackbar v-model="fileErrorVisible" :timeout="6000" color="error" top>
      {{ fileError }}
    </v-snackbar>
    <!-- File Preview Chip -->
    <div v-if="selectedFiles.length" class="file-preview-container">
      <div v-for="(file, idx) in selectedFiles" :key="idx" class="file-preview-chip" :class="{'invalid-file': !file._valid}">
        <i class="mdi mdi-paperclip file-icon"></i>
        <span class="file-name">{{ file.name }}</span>
        <button type="button" class="remove-file-button" @click="removeFile(idx)">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </div>

    <div class="input-wrapper">
      <input
        ref="messageInput"
        v-model="messageText"
        type="text"
        placeholder="Type your message..."
        :disabled="disabled"
        @keypress.enter="handleSend"
        class="message-input"
      />
      <!-- Hidden file input -->
      <input
        ref="fileInput"
        type="file"
        multiple
        style="display: none"
        @change="handleFileChange"
      />
      <!-- Attachment Button -->
      <button
        type="button"
        @click="triggerFileSelect"
        :disabled="disabled"
        class="attach-button"
      >
        <i class="mdi mdi-paperclip"></i>
      </button>
      <!-- Send Button -->
      <button
        @click="handleSend"
        :disabled="disabled || (!messageText.trim() && selectedFiles.length === 0) || selectedFiles.some(f => !f._valid)"
        class="send-button"
      >
        <i class="mdi mdi-send"></i>
      </button>
    </div>
    <div v-if="disabled" class="disabled-message">
      Please select a product to start chatting
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageInput',
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      messageText: '',
      fileError: '',
      fileErrorVisible: false,
      selectedFiles: []
    };
  },
  methods: {
    triggerFileSelect() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const files = event.target.files;
      this.fileError = '';
      this.fileErrorVisible = false;
      if (files && files.length > 0) {
        const allowedExts = ['csv','txt','json','jpg','jpeg','png','gif','xlsx','xls','pdf','doc','mp4','zip','rar','mp3','webp','ppt','psd','lrcat'];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const ext = file.name.split('.').pop().toLowerCase();
          let valid = true;
          if (!allowedExts.includes(ext)) {
            this.fileError = `Unsupported file type .${ext}. Allowed: ${allowedExts.join(', ')}`;
            this.fileErrorVisible = true;
            valid = false;
          }
          if (file.size > 50 * 1024 * 1024) {
            this.fileError = 'File size exceeds 50 MB limit. Please choose a smaller file.';
            this.fileErrorVisible = true;
            valid = false;
          }
if (valid) {
          this.$set(file, '_valid', true);
          this.selectedFiles.push(file);
        } else {
          // Invalid file: do not add to selectedFiles; error already shown
          this.$set(file, '_valid', false);
        }
        }
      }
      event.target.value = '';
    },
    removeFile(index) {
      if (index !== undefined) {
        this.selectedFiles.splice(index, 1);
      } else {
        this.selectedFiles = [];
      }
    },
    handleSend() {
      if (this.disabled || (!this.messageText.trim() && this.selectedFiles.length === 0)) {
        return;
      }
      // Ensure URLs are cached for all selected files (all are valid)
      window.attachmentUrls = window.attachmentUrls || {};
      // Cache URLs for each selected file
      this.selectedFiles.forEach(f => {
        const fileUrl = URL.createObjectURL(f);
        this.$set(window.attachmentUrls, f.name, fileUrl);
      });

      // Emit a separate message for each attachment (but actually emit payload with files)
      this.$emit('send-message', { text: this.messageText.trim(), files: [...this.selectedFiles] });

      // Clear state
      this.messageText = '';
      this.selectedFiles = [];
      // Keep focus on input for next message
      this.$nextTick(() => {
        this.$refs.messageInput.focus();
      });
    }
  },
  mounted() {
    if (!this.disabled) {
      this.$refs.messageInput.focus();
    }
  },
  watch: {
    disabled(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.$refs.messageInput.focus();
        });
      }
    }
  }
}
</script>

<style scoped>
.message-input-container {
  border-top: 1px solid #e0e0e0;
  background: white;
  padding: 16px;
}

.input-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.message-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 24px;
  font-size: 15px;
  outline: none;
  transition: all 0.3s;
  font-family: inherit;
}

.message-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.message-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  color: #999;
}

.message-input::placeholder {
  color: #999;
}

.send-button,
.attach-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  flex-shrink: 0;
}

.send-button:hover:not(:disabled),
.attach-button:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.send-button:active:not(:disabled),
.attach-button:active:not(:disabled) {
  transform: scale(0.95);
}

.send-button:disabled,
.attach-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.send-button i,
.attach-button i {
  font-size: 20px;
}

.disabled-message {
  text-align: center;
  color: #999;
  font-size: 13px;
  margin-top: 8px;
  font-style: italic;
}

/* File Preview Styles */
.file-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.file-preview-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #f3f0fc;
  border: 1px solid #dcd3f9;
  border-radius: 20px;
  color: #5c3db5;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(118, 75, 162, 0.08);
  animation: chip-in 0.25s ease-out forwards;
}

.file-icon {
  font-size: 16px;
}

.file-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file-button {
  background: none;
  border: none;
  padding: 0;
  color: #8c82ab;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, transform 0.2s;
}

.remove-file-button:hover {
  color: #ff4d4d;
  transform: scale(1.1);
}

.remove-file-button i {
  font-size: 16px;
}

.invalid-file {
  background: #ffe5e5;
  border: 1px solid #ff4d4d;
  color: #b00020;
}

@keyframes chip-in {
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>